import { selectCurrentHotelId, selectIsInternalAdmin } from 'accessors'
import Button from 'components/ReusableComponents/Button'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import FilePreview from 'components/ReusableComponents/Messaging/ConversationBubble/FilePreview'
import Toggle from 'components/ReusableComponents/Toggle'
import {
  features,
  useUnlockService
} from 'components/ReusableComponents/UnlockServiceDialog'
import CampaignHistory from 'features/CampaignHistory'
import MediaUploader from 'features/ManageCampaigns/MediaUploader'
import { TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import { getImgUrlFromCDN } from 'features/ManageCampaigns/utils/campaignHelpers'
import { mediaTags } from 'features/ManageCampaigns/utils/constants'
import useEsc from 'hooks/useEsc'
import useFirstRender from 'hooks/useFirstRender'
import usePlausible from 'hooks/usePlausible'
import campaignNamesForCustomer, {
  campaignNamesForInternalAdmin
} from 'main/campaignNamesAndDesc'
import { DEFAULT_SUBSIDIARY_ID } from 'main/globalVariables'
import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import campaignApi from 'services/api/campaign'

import styles from './EditTextDialog.module.scss'
import TextArea from './TextArea'
import { TextPreview } from './TextPreview'

const mediaTag = mediaTags.campaign

const urlRegex = /^(http|https)?:\/\/\S+/i

function getCampaignName(key, isInternalAdmin) {
  return isInternalAdmin
    ? campaignNamesForInternalAdmin[key] || key
    : campaignNamesForCustomer[key] || key
}

const EditCampaignContent = ({
  canBePublished,
  onSaveText,
  defaultData,
  hasOptOutText = false,
  hasLink = false,
  tags = [],
  requiredTagIds = [],
  alternateReviewLink,
  children,
  canHaveImages = false,
  category,
  campaignMedia: defaultCampaignMedia,
  hasReviewLinkControl,
  campaigns = [],
  isDefaultCampaigns
}) => {
  const [isCustomReviewEnabled, setIsCustomReviewEnabled] = useState(
    !!alternateReviewLink
  )

  const { postEvent, plausibleEvents } = usePlausible()

  useFirstRender(() => {
    postEvent(plausibleEvents.ViewCampaignEditor, {
      category
    })
  }, [category, postEvent, plausibleEvents.ViewCampaignEditor])

  const history = useHistory()

  const [customReviewLink, setCustomReviewLink] = useState(alternateReviewLink)

  const [isSpamCheckDialogVisible, setIsSpamCheckDialogVisible] = useState(false)

  const [isLoadingSpamCheckResults, setIsLoadingSpamCheckResults] = useState(true)

  const [spamResults, setSpamResults] = useState({})

  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  const title = `Editing text for ${getCampaignName(category, isInternalAdmin)}`

  const [campaignsToEdit, setCampaignsToEdit] = useState(
    [...campaigns].map(c => ({
      ...c,
      remainingTags: [],
      messageTemplate: c.messageTemplate.replace(/[\r]/g, '')
    }))
  )

  const [focusedCampaignId, setFocusedCampaignId] = useState(campaignsToEdit[0].contentId)

  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const [isEditHistoryVisible, setIsEditHistoryVisible] = useState(false)
  const [categoryToShowHistory, setCategoryToShowHistory] = useState('')
  const [contentIdToShowHistory, setContentIdToShowHistory] = useState()

  function checkForSpam({ messageTemplate, hasMedia, category }) {
    setIsSpamCheckDialogVisible(true)
    setIsLoadingSpamCheckResults(true)

    campaignApi
      .spamCheck({
        proposedContent: messageTemplate,
        proposedHasMedia: hasMedia,
        category
      })
      .then(res => {
        setIsLoadingSpamCheckResults(false)
        setSpamResults(res)
      })
  }

  function handleClickShowHistory(category, contentId) {
    setCategoryToShowHistory(category)
    setContentIdToShowHistory(contentId)
    setIsEditHistoryVisible(true)
  }

  const wrapperRef = useRef()

  const isCustomReviewLinkValid = customReviewLink
    ? !!customReviewLink.match(urlRegex)
    : false

  function handleChangeCustomReviewToggle(e) {
    setIsCustomReviewEnabled(e.target.checked)
    if (!e.target.check) {
      setCustomReviewLink(null)
    }
  }

  useEsc(() => {
    if (wrapperRef.current.offsetParent !== null) {
      history.goBack()
    }
  })

  function handleCampaignChange(contentId, text) {
    const updatedCampaigns = campaignsToEdit.map(campaign => {
      if (campaign.contentId === contentId) {
        return {
          ...campaign,
          messageTemplate: text
        }
      }
      return campaign
    })

    setCampaignsToEdit(updatedCampaigns)
  }

  function handleUpdateCampaignTags(contentId, usedTags) {
    setCampaignsToEdit(existingCampaigns =>
      existingCampaigns.map(campaign => {
        if (campaign.contentId === contentId) {
          return {
            ...campaign,
            remainingTags: tags
              .filter(t => requiredTagIds.includes(t.id))
              .filter(t => !usedTags.find(u => u.id === t.id))
          }
        }
        return campaign
      })
    )
  }

  function getCampaignTitle(campaign) {
    if (campaign.type === 'consentRequest') {
      return 'Default text'
    }

    if (campaign.type === 'nonOTAVariant') {
      return 'For direct guests'
    }

    if (campaign.type === 'OTAVariant') {
      return 'For OTA guests'
    }

    if (campaign.category === 'MissingDetailReminder1') {
      return 'Next day reminder'
    }

    if (campaign.category === 'MissingDetailReminder2') {
      return 'Next week reminder'
    }

    if (campaign.category === 'OptInRequest') {
      return 'Widget consent request'
    }

    if (campaign.category === 'OptedIn') {
      return 'Widget welcome'
    }

    if (campaign.category === 'ReviewReminder1') {
      return 'Reminder (one day after request)'
    }

    if (campaign.category === 'ReviewReminder2') {
      return '2nd reminder (two days after request)'
    }

    return campaignNamesForCustomer[campaign.category] || campaign.category
  }

  const [campaignMedia, setCampaignMedia] = useState(defaultCampaignMedia)

  const hotelId = useSelector(selectCurrentHotelId)

  function saveText() {
    if (campaignsToEdit.some(c => c.remainingTags.length > 0)) {
      setShowValidationErrors(true)
      return
    }
    if (isCustomReviewEnabled && !isCustomReviewLinkValid) {
      setShowValidationErrors(true)
      return
    }
    if (hasReviewLinkControl) {
      campaignApi.updateAlternateReviewLink({
        alternateReviewLink: customReviewLink
      })
    }
    if (canHaveImages) {
      onSaveText(campaignsToEdit[0].messageTemplate, campaignMedia)
    } else {
      onSaveText(campaignsToEdit)
    }
  }

  function goToPublish(category) {
    if (campaignsToEdit.some(c => c.remainingTags.length > 0)) {
      setShowValidationErrors(true)
      return
    }
    if (isCustomReviewEnabled && !isCustomReviewLinkValid) {
      setShowValidationErrors(true)
      return
    }
    if (hasReviewLinkControl) {
      campaignApi.updateAlternateReviewLink({
        alternateReviewLink: customReviewLink
      })
    }

    campaignApi
      .update({
        subsidiaryId: DEFAULT_SUBSIDIARY_ID,
        categories: [category],
        details: {
          messageTemplate: campaignsToEdit.find(c => c.category === category)
            .messageTemplate
        }
      })
      .then(() => {
        history.push(`/publish-campaign/${category}`)
      })
  }

  return (
    <div className={styles.editTextHolder} ref={wrapperRef}>
      {isSpamCheckDialogVisible && (
        <ConfirmationDialog
          onClose={() => setIsSpamCheckDialogVisible(false)}
          title={
            isLoadingSpamCheckResults
              ? 'Testing...'
              : spamResults.isSpamPredicted
              ? 'Spam'
              : 'Not spam'
          }
          hasConfirmButton={!isLoadingSpamCheckResults}
        >
          {isLoadingSpamCheckResults && <LoadingIndicator />}
          {!isLoadingSpamCheckResults && (
            <>
              <p>{spamResults.numberOfExperiments} experiments conducted</p>
              <p>
                Results: {spamResults.numberOfSpamPredicted} spam,{' '}
                {spamResults.numberOfNoSpamPredicted} not spam
              </p>
              <p>Spam rate: {spamResults.spamRate}</p>
            </>
          )}
        </ConfirmationDialog>
      )}
      {isEditHistoryVisible && (
        <CampaignHistory
          contentId={contentIdToShowHistory}
          category={categoryToShowHistory}
          onClose={() => setIsEditHistoryVisible(false)}
          isDefaultCampaigns={isDefaultCampaigns}
        />
      )}
      <CustomHeader title={title} width={950} />
      <div className={styles.editTextWrapper}>
        <div className={styles.editTextContent}>
          {campaignsToEdit.map((campaign, index) => (
            <div className={styles.editTextForm} key={campaign.contentId}>
              <p className={styles.editTextFormTitle}>
                {getCampaignTitle(campaign)}

                {isInternalAdmin && (
                  <button
                    className={styles.showHistoryButton}
                    onClick={() => handleClickShowHistory(category, campaign.contentId)}
                  >
                    History
                  </button>
                )}
                {isInternalAdmin && (
                  <button
                    className={styles.spamTestButton}
                    onClick={() =>
                      checkForSpam({
                        messageTemplate: campaign.messageTemplate,
                        hasMedia: false,
                        category: campaign.category
                      })
                    }
                  >
                    Spam check
                  </button>
                )}
                {canBePublished && (
                  <button
                    className={styles.publishButton}
                    onClick={() => goToPublish(campaign.category)}
                  >
                    Publish
                  </button>
                )}
              </p>{' '}
              <TextArea
                key={campaign.contentId}
                handleChange={text => handleCampaignChange(campaign.contentId, text)}
                handleUpdateUsedTags={tags =>
                  handleUpdateCampaignTags(campaign.contentId, tags)
                }
                disabled={!isInternalAdmin && campaign.type === 'consentRequest'}
                value={campaign.messageTemplate}
                baseTags={tags}
                requiredTagIds={requiredTagIds}
                shouldShowOptOutText={hasOptOutText}
                isInternalAdmin={isInternalAdmin}
                onFocus={() => setFocusedCampaignId(campaign.contentId)}
                autoFocus={
                  !isInternalAdmin &&
                  campaignsToEdit.some(c => c.type === 'consentRequest')
                    ? index === 1
                    : index === 0
                }
              />
            </div>
          ))}
          {canHaveImages && (
            <div className={styles.mediaUploaderHolder}>
              <MediaUploader
                selectedMedia={campaignMedia}
                setSelectedMedia={setCampaignMedia}
                removeSelectedMedia={() => setCampaignMedia(null)}
                mediaTag={mediaTag}
                acceptedFileTypes={[
                  'image/*',
                  'application/pdf',
                  'application/rtf',
                  'text/calendar',
                  'text/vcard',
                  'video/*'
                ]}
              />
            </div>
          )}
          {children}
          {hasReviewLinkControl && (
            <div className={styles.customReviewLinkHolder}>
              <Toggle
                label="Use custom review link"
                checked={isCustomReviewEnabled}
                handleChange={handleChangeCustomReviewToggle}
                className={styles.customReviewLinkToggle}
              />
              <p className={styles.customReviewLinkHint}>
                Enable this if you want to send your own review links.
              </p>
              {isCustomReviewEnabled && (
                <div>
                  <TextInput
                    className={styles.customReviewLinkInput}
                    label="Custom review link"
                    autoFocus
                    value={customReviewLink || ''}
                    onChange={e => setCustomReviewLink(e.target.value)}
                    valid={isCustomReviewLinkValid}
                  />
                  {customReviewLink && !isCustomReviewLinkValid && (
                    <p className={styles.customRevieLinkError}>
                      Enter a valid link starting with http or https
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {showValidationErrors &&
          campaignsToEdit.map(c => (
            <>
              {c.remainingTags.length > 0 && (
                <div className={styles.validationErrorContainer}>
                  <InfoIcon className={styles.warnIcon} fill="red" />
                  <div className={styles.validationError}>
                    <span>Use </span>
                    <div className={styles.remainingTags}>
                      {c.remainingTags.map((t, i) => (
                        <>
                          {c.remainingTags.length > 1 &&
                          i === c.remainingTags.length - 1 ? (
                            <span> and </span>
                          ) : (
                            i > 0 && <span> , </span>
                          )}{' '}
                          <span className={styles.requiredTag}>{t.display}</span>
                        </>
                      ))}
                    </div>
                    <span> in </span>
                    <span>the </span>
                    <span className={styles.validationCampaignName}>{c.category} </span>
                    <span>field </span>
                    <span>to </span>
                    <span>save.</span>
                  </div>
                </div>
              )}
            </>
          ))}
        {showValidationErrors &&
          (isCustomReviewEnabled ? !isCustomReviewLinkValid : false) && (
            <div className={styles.validationErrorContainer}>
              <InfoIcon className={styles.warnIcon} fill="red" />
              <div className={styles.validationError}>
                <span>Enter a valid link starting with http or https</span>
              </div>
            </div>
          )}
        <div className={styles.buttonHolder}>
          <Button type="secondary" onClick={history.goBack}>
            Discard
          </Button>
          <Button onClick={saveText}>Save</Button>
        </div>
      </div>
      <TextPreview>
        <TextPreviewBubble
          defaultData={defaultData}
          hasOptOutText={hasOptOutText}
          hasLink={hasLink}
          customLink={isCustomReviewEnabled ? customReviewLink : null}
          text={
            campaignsToEdit.find(c => c.contentId === focusedCampaignId).messageTemplate
          }
        />
        {campaignMedia && (
          <>
            <FilePreview
              src={getImgUrlFromCDN(campaignMedia, mediaTag, hotelId)}
              fileName={campaignMedia}
              extension={campaignMedia.match(/\.(\w+)$/)[1]}
              clickable={false}
              className={styles.campaignMedia}
              showFileName={true}
            />
          </>
        )}
      </TextPreview>
    </div>
  )
}

export default EditCampaignContent
